import { createContext, useContext, useState, ReactNode } from "react";
import { safeLocalStorage } from "@/lib/safeStorage";

interface PipelineContextType {
  selectedPipelineIds: string[];
  setSelectedPipelineIds: (ids: string[]) => void;
  /** @deprecated Alias para compatibilidade — primeiro ID da lista ou null */
  currentPipelineId: string | null;
  /** @deprecated Alias — usa setSelectedPipelineIds([id]) internamente */
  setCurrentPipelineId: (id: string | null) => void;
}

const PipelineContext = createContext<PipelineContextType | undefined>(undefined);

function loadInitialIds(): string[] {
  // Tentar carregar novo formato
  const saved = safeLocalStorage.getItem("selected-pipeline-ids");
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch { /* ignore */ }
  }

  // Migração de legacy: "current-pipeline-id" → array
  const legacy = safeLocalStorage.getItem("current-pipeline-id");
  if (legacy) {
    safeLocalStorage.setItem("selected-pipeline-ids", JSON.stringify([legacy]));
    safeLocalStorage.removeItem("current-pipeline-id");
    return [legacy];
  }

  return [];
}

export function PipelineProvider({ children }: { children: ReactNode }) {
  const [selectedPipelineIds, setSelectedPipelineIdsState] = useState<string[]>(loadInitialIds);

  const setSelectedPipelineIds = (ids: string[]) => {
    setSelectedPipelineIdsState(ids);
    if (ids.length > 0) {
      safeLocalStorage.setItem("selected-pipeline-ids", JSON.stringify(ids));
    } else {
      safeLocalStorage.removeItem("selected-pipeline-ids");
    }
  };

  // Aliases de compatibilidade
  const currentPipelineId = selectedPipelineIds[0] ?? null;
  const setCurrentPipelineId = (id: string | null) => {
    setSelectedPipelineIds(id ? [id] : []);
  };

  return (
    <PipelineContext.Provider value={{ selectedPipelineIds, setSelectedPipelineIds, currentPipelineId, setCurrentPipelineId }}>
      {children}
    </PipelineContext.Provider>
  );
}

export function usePipelineContext() {
  const context = useContext(PipelineContext);
  if (!context) {
    throw new Error("usePipelineContext must be used within PipelineProvider");
  }
  return context;
}
